import {
   AbstractControl,
   FormArray,
   UntypedFormArray,
   UntypedFormBuilder,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { ReportParameters } from 'generated/graphql-types';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getRefreshTimeSecondsValue } from 'src/app/pages/dashboard/models/dashboard-tile-layout-configuration.model';
import { TileLayoutConfigurationFormValue } from 'src/app/pages/dashboard/tile-layout-configurations/tile-layout-configurations.component';
import { Reports } from '../../reports.enum';
import { ParameterFormValueUnion, sortParameterFormValues } from '../models/report-parameter-form-value';
import {
   convertToGroup,
   ReportParametersUnion,
   TileLayoutConfigurationColorParameter,
   TileLayoutConfigurationNameParameter,
   TileLayoutConfigurationRefreshTimeSecondsParameter,
   TileLayoutConfigurationReportParameters,
   TileLayoutConfigurationTileSetType,
   ViewReportParameters,
} from '../models/report-parameter-union.model';
import { ReportParameterConfig } from '../report-parameter.config';

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface ReportParameterFormService<P> {
   form: UntypedFormGroup;

   config: ReportParameterConfig[];

   savedParameters: ReportParametersUnion[];

   /**
    * Optional.
    * The list of contextArticles to display in the help context. If empty
    * or undefined, the help icon button will not appear.
    */
   contextArticleIds: number[];

   setValue(value: unknown): void;

   getValue(includeAskParameters?: boolean): P | null;

   convertValue(values: ReportParametersUnion[]): P | null;

   getReportParameterConfig(parameterType: ReportParameters, showAskParameterOption: boolean): ReportParameterConfig;

   isListComplete(values: ReportParametersUnion[], includeAskParameters?: boolean): boolean;

   patchSavedValues(values: ReportParametersUnion[], showAskToggle: boolean, showAskParameters: boolean): void;

   resetConfig(): void;

   /**
    * Used to reorder the values of the formServices form.
    * When multiple tiles/reports are on the screen at that same time,
    * the order of the parameters is not always consistent
    */
   orderValues(): void;

   /**
    * Used to add a tiles layout configuration to the formService as controls
    * @param layoutConfiguration The tiles layout configuration to load initially
    */
   initLayoutConfigurationControls(layoutConfiguration: TileLayoutConfigurationFormValue): void;

   /**
    * Used to pull out the layout configuration values into a formatted object
    * @param values The reportparameters to be converted
    */
   getLayoutConfigurations(values: ReportParametersUnion[]): {
      name: string | null;
      color: string | null;
      refreshTimeSeconds: number | null;
   };
}

export abstract class ReportParameterFormServiceBase {
   config: ReportParameterConfig[];

   form: UntypedFormGroup;

   protected form$ = new Subject<UntypedFormGroup>();

   readonly destroyed$ = new Subject<void>();

   private formValueChangeSubscription: Subscription;

   savedParameters: ReportParametersUnion[];

   contextArticleIds: number[] = [];

   constructor(protected fb: UntypedFormBuilder) {
      this.form$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         if (this.formValueChangeSubscription) {
            this.formValueChangeSubscription.unsubscribe();
         }
         this.syncOptions();
         this.formValueChangeSubscription = this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.syncOptions();
         });
      });
   }

   syncOptions(): void {}

   getReportParameterConfig(
      parameterType: ViewReportParameters,
      showAskParameterOption = false,
      specificExtensionFilter = false,
      report?: Reports
   ): ReportParameterConfig {
      switch (parameterType) {
         case ReportParameters.DateRange:
            return { type: 'dateRange' };
         case ReportParameters.ExtensionsToInclude:
            if (specificExtensionFilter) return { type: 'extensionHuntGroups' };
            return report == Reports.InternationalCalls ? { type: 'hidden' } : { type: 'extensions' };
         case ReportParameters.ExtensionGroupsToInclude:
            return { type: 'extensionGroups' };
         case ReportParameters.CallQueueExtensionsToInclude:
            return { type: 'callQueueExtensions' };
         case ReportParameters.ColumnSort:
            return { type: 'columnSort', columnNames: [] };
         case ReportParameters.AutoAttendantExtensionsToInclude:
            return { type: 'autoAttendantExtensions' };
         case ReportParameters.TimeZone:
            return { type: 'hidden' };
         case ReportParameters.DateRangeSelect:
            return { type: 'dateRangeSelect' };
         case ReportParameters.CallDirectionsToInclude:
            return { type: 'callDirections' };
         case ReportParameters.CallDispositionsToInclude:
            return { type: 'callDispositions' };
         case ReportParameters.ExcludeInternal:
            return { label: 'Exclude Internal Calls', type: 'bool' };
         case ReportParameters.PhoneNumbersToInclude:
            return { type: 'phoneNumbers' };
         case ReportParameters.DaysOfTheWeek:
            return { label: 'Days to Include', type: 'daysOfTheWeek' };
         case ReportParameters.NumberOfCallers:
            return { type: 'numberOfCallers' };
         case ReportParameters.SelectedAccount:
            return { type: 'selectedAccount' };
         case ReportParameters.SortBy:
            return { type: 'sortBy' };
         case ReportParameters.ExtensionSortBy:
            return { type: 'extensionSortBy' };
         case ReportParameters.BarGraphSource:
            return { type: 'barGraphSource' };
         case ReportParameters.GroupByExtension:
            return { label: 'Group by Extension', type: 'bool' };
         case ReportParameters.IncludeNotMissedCalls:
            return { label: 'Include selected extensions even if they do not have missed calls', type: 'bool' };
         case ReportParameters.SummaryOnly:
            return { label: 'Show Summary Only', type: 'bool' };
         case ReportParameters.ShowDate:
            return { type: 'bool', label: 'Show Date', showAskParameterOption };
         case ReportParameters.AggregateQueues:
            return { type: 'bool', label: 'Aggregate Queues', showAskParameterOption };
         case ReportParameters.TimeRange:
            return { type: 'timeRange', showAskParameterOption };
         default:
            throw new Error('Error unknown parameter type: ' + parameterType);
      }
   }

   patchSavedValues(values: ReportParametersUnion[], showAskToggle: boolean, showAskParameters: boolean): void {
      let formArray: FormArray;

      if (!showAskParameters) {
         const askParameters = values.filter((v) => {
            return v.isAskParameter;
         });
         this.savedParameters = values.filter((v) => {
            return !v.isAskParameter;
         });
         const patchedConfig = askParameters.map((ap) => {
            return this.getReportParameterConfig(ap.parameterType, showAskToggle);
         });
         this.config = patchedConfig;
         const askParametersFormValue = askParameters.map((ap) => {
            return convertToGroup(ap, this.fb);
         });
         const nonAskParametersFormValue = this.savedParameters.map((ap) => {
            return convertToGroup(ap, this.fb);
         });
         formArray = this.fb.array(
            [...askParametersFormValue, ...nonAskParametersFormValue].map((v) => {
               return this.fb.group(v);
            })
         );
      } else {
         const patchedConfig = values.map((ap) => {
            return this.getReportParameterConfig(ap.parameterType, showAskToggle);
         });
         this.config = patchedConfig;
         const formValue = values.map((ap) => {
            return convertToGroup(ap, this.fb, false);
         });
         formArray = this.fb.array(
            formValue.map((v) => {
               return this.fb.group(v);
            })
         );
      }

      this.form = this.fb.group({ values: formArray });
      this.form$.next(this.form);
   }

   resetConfig(): void {
      const controls = (this.form.get('values') as UntypedFormArray).controls;
      const orderedControls = controls.sort((first, second) => {
         const firstValue = first.value as ParameterFormValueUnion;
         const secondValue = second.value as ParameterFormValueUnion;
         return sortParameterFormValues(firstValue, secondValue);
      });

      this.config = orderedControls.map((v) => {
         const paramValue: ParameterFormValueUnion = v.value;
         return this.getReportParameterConfig(paramValue.parameterType);
      });
   }

   orderValues(): void {
      return;
   }

   initLayoutConfigurationControls(layoutConfiguration: TileLayoutConfigurationFormValue): void {
      const valuesControl = this.form.get('values') as FormArray;
      const layoutConfigurationParameterTypes = [
         TileLayoutConfigurationReportParameters.Name,
         TileLayoutConfigurationReportParameters.Color,
         TileLayoutConfigurationReportParameters.RefreshTimeSeconds,
         TileLayoutConfigurationReportParameters.TileSetType,
      ];
      const values =
         valuesControl.controls.filter((c) => {
            return !layoutConfigurationParameterTypes.some((t) => {
               return t === c.value.parameterType;
            });
         }) ?? [];

      this.form = this.fb.group({
         values: this.fb.array([
            ...values,
            this.fb.group(
               {
                  name: [
                     layoutConfiguration.name,
                     [Validators.required, Validators.maxLength(50), Validators.pattern(/^(?!\s*$).+/)],
                  ],
                  parameterType: TileLayoutConfigurationReportParameters.Name,
               },
               { validators: Validators.required }
            ),
            this.fb.group(
               {
                  color: [layoutConfiguration.color, Validators.required],
                  parameterType: TileLayoutConfigurationReportParameters.Color,
               },
               { validators: Validators.required }
            ),
            this.fb.group({
               refreshTimeSeconds: [
                  layoutConfiguration.refreshTimeSeconds,
                  [Validators.min(1), Validators.pattern(/^\d+$/)],
               ],
               parameterType: TileLayoutConfigurationReportParameters.RefreshTimeSeconds,
            }),
            this.fb.group({
               tileSetType: layoutConfiguration.tileSetType,
               parameterType: TileLayoutConfigurationReportParameters.TileSetType,
            }),
         ]),
      });
      this.form.updateValueAndValidity();
      this.form$.next(this.form);
   }

   getLayoutConfigurations(values: ReportParametersUnion[]): TileLayoutConfigurationFormValue {
      const name = values.find((v) => {
         return v.parameterType === TileLayoutConfigurationReportParameters.Name;
      })! as TileLayoutConfigurationNameParameter;

      const color = values.find((v) => {
         return v.parameterType === TileLayoutConfigurationReportParameters.Color;
      })! as TileLayoutConfigurationColorParameter;

      const refreshTimeSeconds = values.find((v) => {
         return v.parameterType === TileLayoutConfigurationReportParameters.RefreshTimeSeconds;
      }) as TileLayoutConfigurationRefreshTimeSecondsParameter;

      const tileSetType = values.find((v) => {
         return v.parameterType === TileLayoutConfigurationReportParameters.TileSetType;
      }) as TileLayoutConfigurationTileSetType;

      const formValues = this.form.get('values') as UntypedFormArray;
      const controls = formValues?.controls ?? [];
      const layoutConfigurationParameterTypes = [
         TileLayoutConfigurationReportParameters.Name,
         TileLayoutConfigurationReportParameters.Color,
         TileLayoutConfigurationReportParameters.RefreshTimeSeconds,
         TileLayoutConfigurationReportParameters.TileSetType,
      ];
      const layoutConfigurationControls = controls.filter((c) =>
         layoutConfigurationParameterTypes.some((lcpt) => lcpt === c.value.parameterType)
      );
      const dirtyLayoutConfigurations = layoutConfigurationControls.filter((c) => c.dirty);

      return {
         name: name?.name ?? null,
         color: color?.color ?? null,
         refreshTimeSeconds: getRefreshTimeSecondsValue(refreshTimeSeconds?.refreshTimeSeconds),
         tileSetType: tileSetType?.tileSetType,
         fields: dirtyLayoutConfigurations
            .map((dlc) => {
               if (dlc.value.parameterType === TileLayoutConfigurationReportParameters.Name) return 'Name';
               if (dlc.value.parameterType === TileLayoutConfigurationReportParameters.Color) return 'Color';
               if (dlc.value.parameterType === TileLayoutConfigurationReportParameters.RefreshTimeSeconds)
                  return 'RefreshTimeSeconds';
               if (dlc.value.parameterType === TileLayoutConfigurationReportParameters.TileSetType)
                  return 'TileSetType';

               return '';
            })
            .filter((f) => f !== ''),
      };
   }

   nameValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const value = control.value;
         const regex = /^(?!\s*$).+/; // Regex to ensure not only whitespaces
         if (value && !regex.test(value)) {
            return { invalidName: true }; // Return error if the name is invalid
         }
         return null; // Return null if the value is valid
      };
   }
}
